import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Search, Calendar } from 'lucide-react';
import api from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './AuditLogList.css';

const ACTION_CSS = {
  login: 'action-login',
  logout: 'action-logout',
  create: 'action-create',
  update: 'action-update',
  delete: 'action-delete',
  view: 'action-view',
};

function AuditLogList() {
  const { t } = useTranslation(['admin', 'common']);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchLogs(), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, actionFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;

      const res = await api.getAuditLogs(params);
      setLogs(res.data.results || res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading audit log:', err);
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="audit-log-page">
      <div className="audit-log-header">
        <h1><Shield size={24} /> {t('admin:auditLog.title')}</h1>
        <p className="subtitle">{t('admin:auditLog.subtitle')}</p>
      </div>

      {/* Фильтры */}
      <div className="audit-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('admin:auditLog.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          <option value="">{t('admin:auditLog.allActions')}</option>
          {['login', 'logout', 'create', 'update', 'delete', 'view'].map(key => (
            <option key={key} value={key}>{t(`common:actions.${key}`)}</option>
          ))}
        </select>
      </div>

      {/* Лог */}
      {loading ? (
        <div className="loading-spinner">{t('common:table.loading')}</div>
      ) : (
        <div className="audit-table-wrapper">
          <table className="audit-table">
            <thead>
              <tr>
                <th>{t('admin:auditLog.date')}</th>
                <th>{t('admin:auditLog.user')}</th>
                <th>{t('admin:auditLog.actionType')}</th>
                <th>{t('admin:auditLog.object')}</th>
                <th>{t('admin:auditLog.description')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan="5" className="empty-cell">{t('admin:auditLog.noRecords')}</td></tr>
              ) : (
                logs.map(log => {
                  const actionLabel = t(`common:actions.${log.action}`, { defaultValue: log.action_display || log.action });
                  const actionCss = ACTION_CSS[log.action] || '';
                  return (
                    <tr key={log.id}>
                      <td className="date-cell">
                        <Calendar size={14} />
                        {formatDateTime(log.created_at)}
                      </td>
                      <td>{log.user_name || '—'}</td>
                      <td>
                        <span className={`action-badge ${actionCss}`}>
                          {actionLabel}
                        </span>
                      </td>
                      <td>{log.model_name || '—'}</td>
                      <td className="desc-cell">{log.description || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AuditLogList;
